import { Flex, FlexProps, Heading, HeadingProps } from "@chakra-ui/react";

import { MapResult } from "@/api";

import { REPORT_BLOCK_GAP, REPORT_BLOCK_INTERNAL_GAP } from "../consts/sizes";

export const ReportBlock = ({ children, ...rest }: FlexProps) => {
  return (
    <Flex gap={REPORT_BLOCK_GAP} {...rest}>
      {children}
    </Flex>
  );
};

const BASE_CHILD_PROPS: FlexProps = {
  bg: "brand.bg",
  gap: REPORT_BLOCK_INTERNAL_GAP,
  flexDir: "column",
  position: "relative",
};

type ReportChildPropsNonExtend = {
  title: string | undefined;
  isMap?: boolean;
};
type ReportChildProps = FlexProps & ReportChildPropsNonExtend;

const getChildProps = ({ isMap }: ReportChildProps): FlexProps => {
  if (isMap) return BASE_CHILD_PROPS;

  return {
    ...BASE_CHILD_PROPS,
    p: REPORT_BLOCK_GAP,
  };
};

const ReportBlockLabelValue = ({
  labelColor,
  labelPrimair,
}: {
  labelColor: string | undefined;
  labelPrimair: string | undefined;
}) => {
  if (!labelColor) return null;

  return (
    <Flex bg={labelColor} color="white" py={2} alignItems="center" justifyContent="center">
      {labelPrimair}
    </Flex>
  );
};

ReportBlock.LabelValue = ReportBlockLabelValue;

const ReportBlockLabelCategories = ({ labelCategories }: Pick<MapResult, "labelCategories">) => {
  return (
    <>
      {(labelCategories ?? []).map(({ label, description, color }) => (
        <Flex alignItems="center" key={label + description + color}>
          <Flex
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            bg={color}
            color="white"
            w={5}
            h={5}
            mr={2}
          >
            {label}
          </Flex>
          {description}
        </Flex>
      ))}
    </>
  );
};

ReportBlock.LabelCategories = ReportBlockLabelCategories;

export const MAP_HEADING_SIZE_PX = 38;

const ReportBlockTitle = ({
  title,
  isMap,
  size,
}: ReportChildPropsNonExtend & { size: "sm" | "md" | "lg" }) => {
  if (!title) return null;

  const headingLevel = (() => {
    if (isMap) return "h6";
    if (size === "sm") return "h6";
    if (size === "md") return "h5";
    if (size === "lg") return "h4";
  })();

  const props: HeadingProps = (() => {
    if (isMap)
      return {
        position: "absolute",
        zIndex: 9999,
        bg: "brand.bg",
        top: 0,
        left: 0,
        w: "fit-content",
        p: 2,
      };
    return {};
  })();

  return (
    <Heading
      as={headingLevel}
      size={headingLevel}
      h={isMap ? MAP_HEADING_SIZE_PX + "px" : undefined}
      color="brand.primary"
      {...props}
    >
      {title}
    </Heading>
  );
};

ReportBlock.Title = ReportBlockTitle;

const ReportBlockSmall = (props: ReportChildProps) => {
  const { isMap, title, children, ...rest } = props;
  const childProps = getChildProps(props);
  return (
    <Flex w="60%" {...childProps} {...rest}>
      <ReportBlockTitle {...{ isMap, title }} size="sm" />
      {children}
    </Flex>
  );
};

ReportBlock.Small = ReportBlockSmall;

const ReportBlockMedium = (props: ReportChildProps) => {
  const { isMap, title, children, ...rest } = props;
  const childProps = getChildProps(props);
  return (
    <Flex w="75%" {...childProps} {...rest}>
      <ReportBlockTitle {...{ isMap, title }} size="md" />
      {children}
    </Flex>
  );
};

ReportBlock.Medium = ReportBlockMedium;

const ReportBlockLarge = (props: ReportChildProps) => {
  const { isMap, title, children, ...rest } = props;
  const childProps = getChildProps(props);

  return (
    <Flex w="full" {...childProps} {...rest}>
      <ReportBlockTitle {...{ isMap, title }} size="lg" />
      {children}
    </Flex>
  );
};

ReportBlock.Large = ReportBlockLarge;

const ReportBlockMap = (props: ReportChildProps) => {
  const { isMap, title, children, ...rest } = props;
  const childProps = getChildProps(props);

  return (
    <Flex minH="400px" h="full" w="400px" flexGrow={1} flexShrink={0} {...childProps} {...rest}>
      <ReportBlockTitle {...{ isMap, title }} size="lg" />
      {children}
    </Flex>
  );
};

ReportBlock.Map = ReportBlockMap;
